import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import type { Atelier, FakEditeur, FakFeedback, FakKit, Personne, Pole, StatutFak } from '../types';
import { PoleBadge, PoleChips } from '../components/annuaire/PoleChips';
import { JOURS_SEMAINE, fakBadgeClass, formatDate, formatHoraire } from '../components/annuaire/format';
import { LIBELLE_MODE_COANIMATION } from '../components/demande/coanimation';
import { LIBELLES_STATUT_FAK } from '../components/demande/format';

// Catalogue des ateliers (référentiel : horaires, lieux, titulaires, actif).
// Détail en panneau latéral (panel-slide) avec formulaire atelier + éditeur
// FRA + retours. La bibliothèque FRA vit sur sa propre page (FakPage) ; le
// param ?atelier=<id> ouvre directement le panneau (lien « Modifier la
// fiche » depuis la bibliothèque).
export function AteliersPage({ params }: { params?: Record<string, string> } = {}) {
  const { peutGerer } = useRole();
  const [poles, setPoles] = useState<Pole[]>([]);
  const [ateliers, setAteliers] = useState<Atelier[]>([]);
  const [personnes, setPersonnes] = useState<Personne[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [poleId, setPoleId] = useState<number | null>(null);
  const [actifsSeuls, setActifsSeuls] = useState(true);
  const [filtreFak, setFiltreFak] = useState(false);
  const [q, setQ] = useState('');

  const [detailId, setDetailId] = useState<number | null>(() => {
    const depuisLien = Number(params?.atelier);
    return Number.isInteger(depuisLien) && depuisLien > 0 ? depuisLien : null;
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [meta, ateliersRes, personnesRes] = await Promise.all([
        api.getMeta(),
        api.getAteliers({ pole_id: poleId ?? undefined, actif: actifsSeuls ? true : undefined }),
        api.getPersonnes({ actif: true, externe: false }),
      ]);
      setPoles(meta.poles);
      setAteliers(ateliersRes);
      setPersonnes(personnesRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec du chargement');
    } finally {
      setLoading(false);
    }
  }, [poleId, actifsSeuls]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const qNorm = q.trim().toLowerCase();
  const visibles = ateliers
    .filter((a) => !filtreFak || a.fak_statut === 'manquant' || a.fak_statut === 'a_reviser')
    .filter((a) => !qNorm || [a.nom, a.lieu, a.titulaire_nom].some((v) => v?.toLowerCase().includes(qNorm)));

  const majAtelier = async (id: number, data: Partial<Atelier>) => {
    try {
      const updated = await api.updateAtelier(id, data);
      setAteliers((prev) => prev.map((a) => (a.id === id ? updated : a)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la mise à jour');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading"><span className="page-title">Ateliers</span></div>
      </div>

      <div className="toolbar">
        <PoleChips poles={poles} selected={poleId} onSelect={setPoleId} />
        <button type="button" className={`chip ${actifsSeuls ? 'active' : ''}`} onClick={() => setActifsSeuls((v) => !v)}>Actifs uniquement</button>
        <button type="button" className={`chip ${filtreFak ? 'active' : ''}`} onClick={() => setFiltreFak((v) => !v)}>FRA à réviser/manquantes</button>
        <div className="search-wrapper">
          <input className="search-input" placeholder="Atelier, lieu, titulaire…" aria-label="Rechercher un atelier, un lieu ou un·e titulaire" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <p className="text-xs text-ink-3 mb-12">
        Les ateliers, leurs horaires et leurs titulaires viennent d'Eveil : un nouvel atelier ou un changement
        d'horaire se fait dans le CRM et apparaît ici automatiquement.
      </p>

      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}

      {loading ? (
        <div className="spinner" />
      ) : visibles.length === 0 ? (
        <div className="empty-state">Aucun atelier trouvé.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Nom</th><th>Pôle</th><th>Lieu</th><th>Jour / horaire</th><th>Titulaire</th>
              <th>Maintien a minima</th><th>Actif</th><th>FRA</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((a) => (
              <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setDetailId(a.id)}>
                <td className="font-medium">{a.nom}</td>
                <td><PoleBadge nom={a.pole_nom} couleur={poles.find((p) => p.id === a.pole_id)?.couleur} /></td>
                <td className="text-sm">{a.lieu ?? '—'}</td>
                <td className="text-sm">
                  {a.jour_semaine ? JOURS_SEMAINE[a.jour_semaine] : '—'} {formatHoraire(a.heure_debut, a.heure_fin)}
                  {(a.interval_weeks ?? 1) > 1 && <div className="text-xs text-ink-3">une semaine sur {a.interval_weeks}</div>}
                </td>
                <td className="text-sm">{a.titulaire_nom ?? '—'}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {peutGerer ? (
                    <label className="toggle toggle-compact">
                      <input type="checkbox" className="toggle-input" checked={a.maintien_possible}
                        onChange={() => majAtelier(a.id, { maintien_possible: !a.maintien_possible })} />
                      <span className="toggle-slider" />
                    </label>
                  ) : (a.maintien_possible ? 'Oui' : 'Non')}
                </td>
                <td className="text-sm">{a.actif ? 'Actif' : 'Inactif'}{a.categorie ? <div className="text-xs text-ink-3">{a.categorie}</div> : null}</td>
                <td><span className={fakBadgeClass(a.fak_statut)}>{LIBELLES_STATUT_FAK[a.fak_statut ?? 'manquant']}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detailId != null && (
        <AtelierDetailPanel atelierId={detailId} poles={poles} personnes={personnes} peutGerer={peutGerer}
          onClose={() => setDetailId(null)}
          onAtelierSaved={(a) => setAteliers((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...a } : x)))} />
      )}

    </div>
  );
}

function AtelierDetailPanel({ atelierId, poles, personnes, peutGerer, onClose, onAtelierSaved }: {
  atelierId: number; poles: Pole[]; personnes: Personne[]; peutGerer: boolean;
  onClose: () => void; onAtelierSaved: (a: Atelier) => void;
}) {
  const [atelier, setAtelier] = useState<Atelier | null>(null);
  const [fak, setFak] = useState<FakKit | null>(null);
  const [peutModifierFak, setPeutModifierFak] = useState(false);
  const [peutGererEditeurs, setPeutGererEditeurs] = useState(false);
  const [editeurs, setEditeurs] = useState<FakEditeur[]>([]);
  const [ajoutEditeurId, setAjoutEditeurId] = useState<number | ''>('');
  const [savingEditeur, setSavingEditeur] = useState(false);
  const [feedback, setFeedback] = useState<FakFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [maintienPossible, setMaintienPossible] = useState(false);
  const [isReel, setIsReel] = useState(true);
  const [notesAtelier, setNotesAtelier] = useState('');
  const [savingAtelier, setSavingAtelier] = useState(false);

  const [fakStatut, setFakStatut] = useState<StatutFak>('manquant');
  const [lienDocument, setLienDocument] = useState('');
  const [contenu, setContenu] = useState('');
  const [echelle, setEchelle] = useState('');
  const [savingFak, setSavingFak] = useState(false);

  // Le panneau n'est pas remonté quand on change d'atelier (même instance,
  // juste atelierId qui change) — sans le drapeau `annule`, une réponse
  // lente pour un atelier précédent peut écraser l'atelier qu'on regarde
  // maintenant (même pattern que DemandePage.tsx/UrgencePage.tsx).
  useEffect(() => {
    let annule = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [a, fakDetail] = await Promise.all([api.getAtelier(atelierId), api.getFak(atelierId)]);
        if (annule) return;
        setAtelier(a);
        setMaintienPossible(a.maintien_possible); setIsReel(a.is_reel); setNotesAtelier(a.notes ?? '');
        const f = fakDetail.fak;
        setFak(f);
        setPeutModifierFak(fakDetail.peut_modifier);
        setPeutGererEditeurs(fakDetail.peut_gerer_editeurs);
        if (f) {
          setFakStatut(f.statut); setLienDocument(f.lien_document ?? ''); setContenu(f.contenu ?? ''); setEchelle(f.echelle_difficulte ?? '');
          const fb = await api.getFakFeedback(f.id);
          if (annule) return;
          setFeedback(fb);
        } else {
          setFakStatut('manquant'); setLienDocument(''); setContenu(''); setEchelle(''); setFeedback([]);
        }
        if (fakDetail.peut_gerer_editeurs) {
          const eds = await api.getFakEditeurs(atelierId);
          if (annule) return;
          setEditeurs(eds);
        } else {
          setEditeurs([]);
        }
      } catch (err) {
        if (!annule) setError(err instanceof Error ? err.message : 'Échec du chargement');
      } finally {
        if (!annule) setLoading(false);
      }
    })();
    return () => { annule = true; };
  }, [atelierId]);

  const saveAtelier = async () => {
    setSavingAtelier(true);
    setError(null);
    try {
      const updated = await api.updateAtelier(atelierId, {
        maintien_possible: maintienPossible, is_reel: isReel, notes: notesAtelier.trim() || null,
      });
      setAtelier(updated);
      onAtelierSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'enregistrement");
    } finally {
      setSavingAtelier(false);
    }
  };

  const saveFak = async (statutOverride?: StatutFak) => {
    setSavingFak(true);
    setError(null);
    try {
      const updated = await api.upsertFak(atelierId, {
        statut: statutOverride ?? fakStatut, lien_document: lienDocument || null, contenu: contenu || null,
        echelle_difficulte: echelle || null,
      });
      setFak(updated);
      setFakStatut(updated.statut);
      if (atelier) onAtelierSaved({ ...atelier, fak_statut: updated.statut });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'enregistrement de la FRA");
    } finally {
      setSavingFak(false);
    }
  };

  const toggleIntegre = async (fb: FakFeedback) => {
    if (!fak) return;
    try {
      const updated = await api.setFeedbackIntegre(fak.id, fb.id, !fb.integre);
      setFeedback((prev) => prev.map((x) => (x.id === fb.id ? updated : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la mise à jour du retour');
    }
  };

  const ajouterEditeur = async () => {
    if (ajoutEditeurId === '') return;
    setSavingEditeur(true);
    setError(null);
    try {
      await api.ajouterFakEditeur(atelierId, Number(ajoutEditeurId));
      setEditeurs(await api.getFakEditeurs(atelierId));
      setAjoutEditeurId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'ajout");
    } finally {
      setSavingEditeur(false);
    }
  };

  const retirerEditeur = async (personneId: number) => {
    setSavingEditeur(true);
    setError(null);
    try {
      await api.retirerFakEditeur(atelierId, personneId);
      setEditeurs((prev) => prev.filter((e) => e.personne_id !== personneId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec du retrait');
    } finally {
      setSavingEditeur(false);
    }
  };

  return (
    <div className="panel-overlay open">
      <div className="panel-overlay-bg" onClick={onClose} />
      <div className="panel-slide open">
        <div className="panel-slide-head">
          <div className="panel-slide-title">{atelier?.nom ?? '…'}</div>
          <button className="panel-slide-close" onClick={onClose}>✕</button>
        </div>
        <div className="panel-slide-body">
          {loading && <div className="spinner" />}
          {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}
          {atelier && (
            <>
              <div className="panel-section">
                <h4 className="mb-8">Atelier</h4>
                <dl className="text-sm mb-12" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                  <dt className="text-ink-3">Offre</dt><dd style={{ margin: 0 }}>{atelier.offering_nom ?? atelier.nom}{atelier.categorie ? ` · ${atelier.categorie}` : ''}</dd>
                  <dt className="text-ink-3">Pôle</dt><dd style={{ margin: 0 }}>{poles.find((p) => p.id === atelier.pole_id)?.nom ?? '—'}</dd>
                  <dt className="text-ink-3">Lieu</dt><dd style={{ margin: 0 }}>{[atelier.lieu, atelier.salle].filter(Boolean).join(' · ') || '—'}</dd>
                  <dt className="text-ink-3">Créneau</dt>
                  <dd style={{ margin: 0 }}>
                    {atelier.jour_semaine ? JOURS_SEMAINE[atelier.jour_semaine] : '—'} {formatHoraire(atelier.heure_debut, atelier.heure_fin)}
                    {(atelier.interval_weeks ?? 1) > 1 ? ` · une semaine sur ${atelier.interval_weeks}` : ''}
                  </dd>
                  <dt className="text-ink-3">Titulaire</dt>
                  <dd style={{ margin: 0 }}>{personnes.find((p) => p.id === atelier.titulaire_personne_id)?.display_name ?? atelier.titulaire_nom ?? '—'}{atelier.co_animation
                    ? ` · co-animation avec ${personnes.find((p) => p.id === atelier.co_animateur_personne_id)?.display_name ?? '—'} (${atelier.mode_coanimation ? LIBELLE_MODE_COANIMATION[atelier.mode_coanimation].toLowerCase() : 'binôme ou alternance à définir dans Administration'})`
                    : ''}</dd>
                  <dt className="text-ink-3">Statut</dt><dd style={{ margin: 0 }}>{atelier.actif ? 'Actif' : 'Inactif'}</dd>
                </dl>
                <div className="text-xs text-ink-3 mb-12">Ces informations viennent d'Eveil : modifiez-les dans le CRM.</div>
                <label className="toggle mb-8">
                  <input type="checkbox" className="toggle-input" checked={maintienPossible} disabled={!peutGerer}
                    onChange={(e) => setMaintienPossible(e.target.checked)} />
                  <span className="toggle-slider" /><span className="toggle-label">Maintien a minima possible (présence contenante)</span>
                </label>
                <label className="toggle mb-8">
                  <input type="checkbox" className="toggle-input" checked={isReel} disabled={!peutGerer}
                    onChange={(e) => setIsReel(e.target.checked)} />
                  <span className="toggle-slider" /><span className="toggle-label">Atelier remplaçable (décocher pour le temps institutionnel, ex. Topo)</span>
                </label>
                <div className="form-group">
                  <label className="form-label">Note du portail</label>
                  <textarea className="form-textarea" value={notesAtelier} onChange={(e) => setNotesAtelier(e.target.value)} disabled={!peutGerer} />
                </div>
                {peutGerer && (
                  <button className="btn btn-accent mt-8" disabled={savingAtelier} onClick={saveAtelier}>
                    {savingAtelier ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                )}
              </div>

              <div className="panel-section">
                <h4 className="mb-8">FRA (fiche relais d'atelier)</h4>
                {peutModifierFak ? (
                  <>
                    <div className="form-group">
                      <label className="form-label">Statut</label>
                      <select className="form-select" value={fakStatut}
                        onChange={(e) => setFakStatut(e.target.value as StatutFak)}>
                        <option value="manquant">Manquant</option>
                        <option value="brouillon">Brouillon</option>
                        <option value="publie">Publié</option>
                        <option value="a_reviser">À réviser</option>
                        <option value="archive">Archivé</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Lien document</label>
                      <input className="form-input" value={lienDocument} onChange={(e) => setLienDocument(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Contenu (3-6 thématiques transmissibles)</label>
                      <textarea className="form-textarea" value={contenu} onChange={(e) => setContenu(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Échelle de difficulté</label>
                      <input className="form-input" value={echelle} onChange={(e) => setEchelle(e.target.value)} />
                    </div>
                    {fak?.prochaine_revue && (
                      <p className="text-sm text-muted">Prochaine revue : {formatDate(fak.prochaine_revue)}</p>
                    )}
                    <div className="flex gap-8 mt-8">
                      <button className="btn" disabled={savingFak} onClick={() => saveFak()}>Enregistrer le brouillon</button>
                      <button className="btn btn-accent" disabled={savingFak} onClick={() => saveFak('publie')}>Publier</button>
                    </div>
                  </>
                ) : (
                  <FicheFakLecture fak={fak} />
                )}
              </div>

              {peutGererEditeurs && (
                <div className="panel-section">
                  <h4 className="mb-8">Éditeurs délégués</h4>
                  <p className="text-sm text-muted mb-8">
                    En plus de vous-même et de la gestion, ces personnes peuvent modifier la FRA de cet atelier.
                  </p>
                  {editeurs.length === 0 && <p className="text-sm text-muted mb-8">Aucun·e éditeur·rice délégué·e.</p>}
                  {editeurs.map((e) => (
                    <div key={e.personne_id} className="flex items-center justify-between mb-4">
                      <span className="text-sm">{e.personne_nom}</span>
                      <button className="btn btn-ghost btn-sm" disabled={savingEditeur} onClick={() => retirerEditeur(e.personne_id)}>Retirer</button>
                    </div>
                  ))}
                  <div className="flex gap-8 mt-8">
                    <select className="form-select" value={ajoutEditeurId}
                      onChange={(e) => setAjoutEditeurId(e.target.value === '' ? '' : Number(e.target.value))}>
                      <option value="">Ajouter une personne…</option>
                      {personnes
                        .filter((p) => p.id !== atelier?.titulaire_personne_id && !editeurs.some((e) => e.personne_id === p.id))
                        .map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                    </select>
                    <button className="btn btn-accent" disabled={ajoutEditeurId === '' || savingEditeur} onClick={ajouterEditeur}>Ajouter</button>
                  </div>
                </div>
              )}

              <div className="panel-section">
                <h4 className="mb-8">Retours FRA</h4>
                {feedback.length === 0 && <div className="text-sm text-muted">Aucun retour.</div>}
                {feedback.map((fb) => (
                  <div key={fb.id} className="mb-12">
                    <div className="text-sm">{fb.contenu}</div>
                    <div className="flex items-center justify-between text-xs text-muted mt-4">
                      <span>{fb.auteur_nom} — {formatDate(fb.created_at)}</span>
                      {peutGerer && (
                        <label className="toggle toggle-compact">
                          <input type="checkbox" className="toggle-input" checked={fb.integre} onChange={() => toggleIntegre(fb)} />
                          <span className="toggle-slider toggle-slider-ok" />
                          <span className="toggle-label">Intégré</span>
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Fiche de préparation pour un·e remplaçant·e potentiel·le : la même
// information que le formulaire d'édition, présentée en lecture — pas un
// formulaire grisé, une aide (la FRA est écrite par le/la titulaire pour ses
// futur·e·s remplaçant·e·s).
function FicheFakLecture({ fak }: { fak: FakKit | null }) {
  // Le statut « manquant » ne veut pas forcément dire « rien n'a été
  // écrit » — un brouillon peut avoir été enregistré avec ce statut resté
  // par défaut. On se base sur le contenu réel, pas uniquement le statut,
  // sinon on cacherait une aide déjà utile à un·e remplaçant·e.
  const rienASaisir = !fak || (!fak.contenu && !fak.lien_document && !fak.echelle_difficulte);
  if (rienASaisir) {
    return <p className="text-sm text-muted">Aucune FRA disponible pour cet atelier pour l'instant.</p>;
  }
  return (
    <div>
      <span className={fakBadgeClass(fak.statut)}>{LIBELLES_STATUT_FAK[fak.statut]}</span>
      {fak.contenu && (
        <p className="text-sm mt-8" style={{ whiteSpace: 'pre-wrap' }}>{fak.contenu}</p>
      )}
      {fak.echelle_difficulte && (
        <p className="text-sm mt-8"><strong>Échelle de difficulté :</strong> {fak.echelle_difficulte}</p>
      )}
      {fak.lien_document && (
        <p className="text-sm mt-8">
          <a href={fak.lien_document} target="_blank" rel="noreferrer">Ouvrir le document complet</a>
        </p>
      )}
      <p className="text-xs text-muted mt-8">Dernière mise à jour : {formatDate(fak.updated_at)}</p>
    </div>
  );
}
